import type { DerivedContext, CreateDerivedContextBody } from '@lib/api-types/derived-contexts';
import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { ApiError } from '../api/client.ts';
import { ClusterPatterns } from '../components/ClusterPatterns.tsx';
import { RecomputeRatingsButton } from '../components/RecomputeRatingsButton.tsx';
import {
  useDerivedContexts,
  useCreateDerivedContext,
  useDeleteDerivedContext,
  useUpdateDerivedContext,
} from '../view-models/derived-contexts.ts';
import { useFileClusters } from '../view-models/file-clusters.ts';

import type { SettingsOutletContext } from './Settings.tsx';

/**
 * DerivedContexts section of /settings/setup — CRUD for derivedContexts. Same shape as
 * SettingsBusinessFunctions + SettingsEngineers; the legacy bulk
 * `restructure` op continues to seed derivedContexts but per-row
 * authoring goes through this surface.
 *
 * Skills inside a derivedContext are still owned by `restructure` (the
 * bulk seed op) until a per-entity skill slice lands; this tab only
 * manages display + subtitle.
 */
export function SettingsDerivedContexts() {
  const { orgSlug, role } = useOutletContext<SettingsOutletContext>();
  const list = useDerivedContexts(orgSlug);
  const clusters = useFileClusters(orgSlug);
  const canMutate = role === 'admin';

  return (
    <div className="stack" style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      {canMutate ? (
        <section>
          <RecomputeRatingsButton orgSlug={orgSlug} />
          <p className="muted" style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
            Re-derives observations + scores from the current
            cluster set. Run after editing clusters or derivedContexts
            to see the matrix reflect the changes.
          </p>
        </section>
      ) : null}
      <section>
        <h2 style={{ marginBottom: '0.25rem' }}>Contexts</h2>
        <p className="muted" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
          The skill clusters this org tracks — the rows of the
          Resilience matrix. Each derivedContext groups related skills the
          team is rated against.
        </p>
        {list.isPending ? (
          <p className="muted">Loading…</p>
        ) : list.isError ? (
          <ErrorBanner error={list.error} />
        ) : (
          <DerivedContextsList
            items={list.data}
            orgSlug={orgSlug}
            canMutate={canMutate}
            clusters={clusters.data}
          />
        )}
      </section>
      {canMutate ? (
        <section>
          <h2 style={{ marginBottom: '0.25rem' }}>Add a context</h2>
          <p className="muted" style={{ marginTop: 0, marginBottom: '0.75rem' }}>
            Use a short slug (lowercase, hyphens) — e.g. <code>backend</code>{' '}
            or <code>mobile</code>.
          </p>
          <CreateForm orgSlug={orgSlug} />
        </section>
      ) : (
        <p className="muted">
          Only org owners can add, rename, or remove derivedContexts.
        </p>
      )}
    </div>
  );
}

function DerivedContextsList({
  items,
  orgSlug,
  canMutate,
  clusters,
}: {
  items: DerivedContext[];
  orgSlug: string;
  canMutate: boolean;
  clusters: import('../view-models/file-clusters.ts').FileCluster[] | undefined;
}) {
  if (items.length === 0) {
    return <p className="muted">No contexts yet.</p>;
  }
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {items.map((c) => (
        <DerivedContextRow
          key={c.id}
          comp={c}
          orgSlug={orgSlug}
          canMutate={canMutate}
          clusters={clusters}
        />
      ))}
    </ul>
  );
}

function DerivedContextRow({
  comp,
  orgSlug,
  canMutate,
  clusters,
}: {
  comp: DerivedContext;
  orgSlug: string;
  canMutate: boolean;
  clusters: import('../view-models/file-clusters.ts').FileCluster[] | undefined;
}) {
  const [editing, setEditing] = useState(false);
  const [display, setDisplay] = useState(comp.display);
  const [subtitle, setSubtitle] = useState(comp.subtitle ?? '');
  const update = useUpdateDerivedContext(orgSlug);
  const del = useDeleteDerivedContext(orgSlug);

  if (editing) {
    return (
      <li style={liStyle}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            update.mutate(
              {
                id: comp.id,
                body: {
                  display,
                  subtitle: subtitle.trim().length > 0 ? subtitle : null,
                },
              },
              { onSuccess: () => setEditing(false) },
            );
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
        >
          <label>
            <span style={labelStyle}>Display name</span>
            <input
              value={display}
              onChange={(e) => setDisplay(e.target.value)}
              required
              style={inputStyle}
            />
          </label>
          <label>
            <span style={labelStyle}>Subtitle</span>
            <input
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="One-line summary"
              style={inputStyle}
            />
          </label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={() => setEditing(false)}>Cancel</button>
            {update.isError ? <ErrorBanner error={update.error} /> : null}
          </div>
        </form>
      </li>
    );
  }

  return (
    <li style={liStyle}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div>
          <strong>{comp.display}</strong>{' '}
          <code style={{ color: 'var(--color-muted, #888)' }}>{comp.id}</code>
          {comp.subtitle ? (
            <div className="muted" style={{ marginTop: '0.25rem' }}>{comp.subtitle}</div>
          ) : null}
        </div>
        {canMutate ? (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button type="button" onClick={() => { setDisplay(comp.display); setSubtitle(comp.subtitle ?? ''); setEditing(true); }}>
              Edit
            </button>
            <button
              type="button"
              disabled={del.isPending}
              onClick={() => {
                if (confirm(`Remove "${comp.display}"? Past matrix views still surface it.`)) {
                  del.mutate(comp.id);
                }
              }}
            >
              {del.isPending ? 'Removing…' : 'Remove'}
            </button>
          </div>
        ) : null}
      </div>
      <ClusterPatterns
        clusters={clusters}
        ownerKind="derived_context"
        ownerId={comp.id}
      />
      {del.isError ? <ErrorBanner error={del.error} /> : null}
    </li>
  );
}

function CreateForm({ orgSlug }: { orgSlug: string }) {
  const create = useCreateDerivedContext(orgSlug);
  const [id, setId] = useState('');
  const [display, setDisplay] = useState('');
  const [subtitle, setSubtitle] = useState('');

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const body: CreateDerivedContextBody = {
          id: id.trim(),
          display: display.trim(),
          ...(subtitle.trim().length > 0 ? { subtitle: subtitle.trim() } : {}),
        };
        create.mutate(body, {
          onSuccess: () => {
            setId('');
            setDisplay('');
            setSubtitle('');
          },
        });
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '40rem' }}
    >
      <label>
        <span style={labelStyle}>Slug</span>
        <input
          value={id}
          onChange={(e) => setId(e.target.value)}
          required
          minLength={1}
          maxLength={120}
          pattern="[A-Za-z0-9][A-Za-z0-9_\-]*"
          placeholder="backend"
          style={inputStyle}
        />
      </label>
      <label>
        <span style={labelStyle}>Display name</span>
        <input
          value={display}
          onChange={(e) => setDisplay(e.target.value)}
          required
          maxLength={200}
          placeholder="Backend"
          style={inputStyle}
        />
      </label>
      <label>
        <span style={labelStyle}>Subtitle (optional)</span>
        <input
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          maxLength={2000}
          placeholder="One-line summary of the context"
          style={inputStyle}
        />
      </label>
      <div>
        <button type="submit" disabled={create.isPending}>
          {create.isPending ? 'Adding…' : 'Add context'}
        </button>
      </div>
      {create.isError ? <ErrorBanner error={create.error} /> : null}
    </form>
  );
}

function ErrorBanner({ error }: { error: unknown }) {
  const message =
    error instanceof ApiError
      ? `${error.message} (${error.status}${error.code ? ` ${error.code}` : ''})`
      : error instanceof Error
        ? error.message
        : 'Unknown error';
  return (
    <div role="alert" style={{ color: '#c33', marginTop: '0.5rem' }}>
      {message}
    </div>
  );
}

const liStyle: React.CSSProperties = {
  border: '1px solid var(--color-border, #ddd)',
  borderRadius: '4px',
  padding: '0.75rem',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.25rem',
  fontSize: '0.85rem',
  color: 'var(--color-muted, #666)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.4rem 0.5rem',
  border: '1px solid var(--color-border, #ddd)',
  borderRadius: '4px',
};
